import React, { useEffect, useState } from "react";
import Breadcrumb from "../../../components/layout/Breadcrumb";
import { Divider } from "@mui/material";
import InfoIcon from "../../../assets/img/info-user-icon.png";
import api from "../../../services/api";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";
import { fetchAllPermissions } from "../../../redux/slice/getAllPermissionListSlice";
import { fetchPermissionsById } from "../../../redux/slice/getPermissionListByIdSlice";
import axiosPrivate from "../../../services/axios/axiosPrivate";
import { useLocation, useParams } from "react-router-dom";
import { useHasPermission } from "../../../hooks/useHasPermission";

function Permission() {
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userId, userName } = location?.state || {};
  const { permissions, status } = useSelector(state => state.Permissions);
  const { permissionsById } = useSelector(state => state.PermissionsById);
  const [checkedPermission, setCheckedPermission] = useState({});

  const canView = useHasPermission("Assign/Remove Permission-view");
  const canEdit = useHasPermission("Assign/Remove Permission-manage");

  useEffect(() => {
    if (canView && (id || userId)) {
      dispatch(fetchAllPermissions());
    }
  }, [dispatch, canView]);

  useEffect(() => {
    if ((id || userId) && canView) {
      dispatch(fetchPermissionsById(id ?? userId));
    }
  }, [dispatch, id, userId, canView]);

  useEffect(() => {
    if (permissionsById?.length > 0) {
      const newCheckboxes = {};
      permissionsById.forEach((perm) => {
        if (perm.canView) {
          newCheckboxes[`view-${userId}-${perm.id}`] = true;
        }
        if (perm.canManage) {
          newCheckboxes[`manage-${userId}-${perm.id}`] = true;
        }
        if (perm.canDelete) {
          newCheckboxes[`delete-${userId}-${perm.id}`] = true;
        }
      });
      // Update the state with the fetched permissions
      setCheckedPermission(newCheckboxes);
    }
  }, [permissionsById, userId]);

  if (!canView) {
    return <p className="perm-msg">You don’t have permission to view permissions of the user!</p>;
  };
  if (!id && !userId) {
    return (
      <p className="server-error-msg">Invalid Access: No User ID provided!</p>
    );
  }

  const handleTogglePermission = async (isChecked, type, userId, permissionId) => {
    if (!canEdit) {
      toast.error("You don't have access to assign/remove permission", {
        className: 'toast-error-custom',
        autoClose: 5000
      });
      return;
    }

    const keyId = `${type}-${userId}-${permissionId}`;

    // Update local state immediately for fast UI 
    setCheckedPermission(prev => {
      const newState = {
        ...prev,
        [keyId]: isChecked
      };

      // Prepare payload inside the state setter to ensure having the newest values
      const payload = {
        userId,
        permissionId,
        "canView": type === "view" ? isChecked : !!newState[`view-${userId}-${permissionId}`],
        "canManage": type === "manage" ? isChecked : !!newState[`manage-${userId}-${permissionId}`],
        "canDelete": type === "delete" ? isChecked : !!newState[`delete-${userId}-${permissionId}`],
      };

      // Fire the API call with the guaranteed latest data
      savePermissionChange(payload, type, isChecked);

      return newState;
    });
  };

  // Separate function to handle the API logic to keep code clean
  const savePermissionChange = async (payload, type, isChecked) => {
    try {
      await axiosPrivate.post(api.permissions.assign, payload);
      if (isChecked) {
        toast.success(`Assigned ${type} permission`, {
          className: "toast-success-custom",
          autoClose: 3000,
        });
      } else {
        toast.info(`Removed ${type} permission`, {
          className: 'toast-error-custom',
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Unable to connect to server", {
        className: 'toast-error-custom',
        autoClose: 5000
      });
    }
  };

  const renderTooltip = (props, description) => (
    <Tooltip id="button-tooltip" {...props}>
      {description}
    </Tooltip>
  );

  return (
    <div className="user-role_section">
      <Breadcrumb />
      <div className="permission-container">
        <Divider sx={{ my: 2 }} />
        <div className="role-data table-responsive">
          {status === 'loading' && (
            <p className="loading">Loading...</p>
          )}

          {status === 'failed' && (
            <p className="server-error-msg">Unable to connect to server</p>
          )}
          {status === 'succeeded' && (
            permissions.length === 0 ? (
              <p className="loading">No record found for permissions</p>
            ) : (
              <table className="table table-hover table-vcenter text-nowrap mb-0">
                <thead>
                  <tr>
                    <th className="table-row" colSpan="4" style={{ border: '1px solid #d1cece' }}>User : {userName}</th>
                  </tr>
                  <tr>
                    <th className="table-row" >PERMISSIONS</th>
                    <th className="table-row" >VIEW</th>
                    <th className="table-row" >EDIT</th>
                    <th className="table-row" >DELETE</th>
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((permission) => (
                    <tr key={permission.id}>
                      <td>{permission.name}<OverlayTrigger
                        placement="right"
                        delay={{ show: 250, hide: 400 }}
                        overlay={(props) => renderTooltip(props, permission.description)}
                      ><img src={InfoIcon} alt="" style={{ width: '15px', marginLeft: '10px' }} />
                      </OverlayTrigger>
                      </td>
                      {/* View Permission checkbox */}
                      <td>
                        <input
                          type="checkbox"
                          className="check"
                          checked={!!checkedPermission[`view-${userId}-${permission.id}`]}
                          disabled={!canEdit}
                          onChange={(e) =>
                            handleTogglePermission(e.target.checked, "view", userId, permission.id)
                          }
                        />
                      </td>
                      {/* Edit Permission checkbox */}
                      <td>
                        <input
                          type="checkbox"
                          className="check"
                          checked={!!checkedPermission[`manage-${userId}-${permission.id}`]}
                          disabled={!canEdit}
                          onChange={(e) =>
                            handleTogglePermission(e.target.checked, "manage", userId, permission.id)
                          }
                        />
                      </td>
                      {/* Delete Permission checkbox */}
                      <td>
                        <input
                          type="checkbox"
                          className="check"
                          checked={!!checkedPermission[`delete-${userId}-${permission.id}`]}
                          disabled={!canEdit}
                          onChange={(e) =>
                            handleTogglePermission(e.target.checked, "delete", userId, permission.id)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}

export default Permission;

